import { useNavigate } from 'react-router-dom';
import { useState, useRef } from 'react';
import { useDraggable } from '@dnd-kit/core';
import type { OdooTask } from '@/types/odoo';
import { usePlanningStore } from '@/stores/planning';
import { useProjectViewStore } from '@/stores/projectView';
import { usePermissionsStore } from '@/stores/permissions';
import { updatePlanningTask, saveMultiday } from '@/api/planning';
import { formatDateKey } from '@/utils/planning';
import { nextBoardDateKeys } from '@/utils/planningWeeks';
import { displayProjectName } from '@/utils/format';
import { MultiDayPopup } from './MultiDayPopup';
import { EasykitTourModal } from './EasykitTourModal';
import { usePlanningZoom } from './PlanningZoom';
import { InstallerChips } from './InstallerChips';
import { PlanningTaskSheet } from './PlanningTaskSheet';
import { useIsMobile } from '@/hooks/useIsMobile';
import { InterventieBand } from '@/components/interventie/InterventieBand';

interface PlanningCardProps {
  task: OdooTask;
  installerId?: number;
  colorOverride?: { bg: string; border: string; dot: string };
  groupPlId?: number;
  /** When set, overrides the global planning_edit permission for THIS card — lets a
   *  host (the dashboard Inplannen modal) keep other projects' cards as read-only
   *  context while only the current project's cards stay draggable/editable.
   *  Undefined → falls back to the global permission (standalone board, unchanged). */
  editable?: boolean;
  /** Read-only overview board ("Per dag"): no edit affordances at all, but a tap on
   *  mobile still opens the action sheet (with its read-only actions). */
  overview?: boolean;
  /** Dashboard scheduler: clicking the card body opens a detail/description popup. */
  onCardClick?: (task: OdooTask) => void;
  /** Fires after the card mutates the board (reschedule / unschedule-to-archive) so a
   *  host can refresh — e.g. re-pull the "Verwijderde taken" list. */
  onTaskMoved?: () => void;
}

/** Rotating palette of background + border colors keyed by project ID */
const PROJECT_COLORS = [
  { bg: 'bg-blue-50', border: 'border-l-blue-500', dot: 'bg-blue-500' },
  { bg: 'bg-orange-50', border: 'border-l-orange-500', dot: 'bg-orange-500' },
  { bg: 'bg-green-50', border: 'border-l-green-500', dot: 'bg-green-500' },
  { bg: 'bg-purple-50', border: 'border-l-purple-500', dot: 'bg-purple-500' },
  { bg: 'bg-red-50', border: 'border-l-red-500', dot: 'bg-red-500' },
  { bg: 'bg-yellow-50', border: 'border-l-yellow-500', dot: 'bg-yellow-500' },
  { bg: 'bg-teal-50', border: 'border-l-teal-500', dot: 'bg-teal-500' },
  { bg: 'bg-pink-50', border: 'border-l-pink-500', dot: 'bg-pink-500' },
  { bg: 'bg-indigo-50', border: 'border-l-indigo-500', dot: 'bg-indigo-500' },
  { bg: 'bg-amber-50', border: 'border-l-amber-500', dot: 'bg-amber-500' },
  { bg: 'bg-cyan-50', border: 'border-l-cyan-500', dot: 'bg-cyan-500' },
  { bg: 'bg-lime-50', border: 'border-l-lime-600', dot: 'bg-lime-600' },
];

export function getProjectColor(projectId: number) {
  const idx = projectId % PROJECT_COLORS.length;
  return PROJECT_COLORS[idx];
}

export function PlanningCard({ task, installerId, colorOverride, groupPlId, editable, overview = false, onCardClick, onTaskMoved }: PlanningCardProps) {
  const navigate = useNavigate();
  const setActiveTask = useProjectViewStore((s) => s.setActiveTask);
  const projectNameMap = usePlanningStore((s) => s.projectNameMap);
  const soNameMap = usePlanningStore((s) => s.soNameMap);
  const werfMap = usePlanningStore((s) => s.werfMap);
  const fetchPlanning = usePlanningStore((s) => s.fetchPlanning);
  const userMap = usePlanningStore((s) => s.userMap);
  const canEditPlanning = usePermissionsStore((s) => s.canFeature('planning_edit'));
  // Per-card edit gate: a host can lock this card to read-only context (other
  // projects in the Inplannen modal). The flag can only REMOVE edit rights — it
  // can never grant them without the global permission.
  const effectiveEditable = !overview && (editable ?? true) && canEditPlanning;
  const canViewProject = usePermissionsStore((s) => s.canView('project'));
  const faseId = task.parent_id ? task.parent_id[0] : 0;
  const isFaseHighlighted = usePlanningStore((s) => faseId !== 0 && s.lastClickedFaseId === faseId);
  const [showPopup, setShowPopup] = useState(false);
  const [showEasykitModal, setShowEasykitModal] = useState(false);
  const [showSheet, setShowSheet] = useState(false);
  const isMobile = useIsMobile();
  const installerDropdownOpen = usePlanningStore((s) => s.installerEditor?.taskId === task.id);
  const cardRef = useRef<HTMLDivElement>(null);
  // The board is CSS-zoomed; pointer deltas are in screen px, so drag and resize
  // previews divide by it or they lag (zoomed out) or overshoot (zoomed in).
  const zoom = usePlanningZoom();
  const [resizeExtend, setResizeExtend] = useState(0);
  // Preview width in the card's own (unzoomed) px, measured from the real cells so
  // it lines up across the week divider on the multi-week board.
  const [resizePreviewPx, setResizePreviewPx] = useState(0);
  const resizeRef = useRef<{ startX: number; colWidth: number } | null>(null);
  const resizeColsRef = useRef(0);
  const stretchDaysRef = useRef(stretchDays);
  stretchDaysRef.current = stretchDays;

  function handleResizeStart(e: React.MouseEvent) {
    e.preventDefault();
    e.stopPropagation();
    const found = cardRef.current?.closest('td');
    if (!found) return;
    const td: HTMLTableCellElement = found;
    const colWidth = td.getBoundingClientRect().width;
    resizeRef.current = { startX: e.clientX, colWidth };
    resizeColsRef.current = 0;
    setResizeExtend(0);

    function onMove(ev: MouseEvent) {
      if (!resizeRef.current) return;
      const delta = ev.clientX - resizeRef.current.startX;
      const cols = Math.max(0, Math.round(delta / resizeRef.current.colWidth));
      resizeColsRef.current = cols;
      setResizeExtend(cols);
      const cells = Array.from(td.parentElement?.children ?? []);
      const target = cells[Math.min(cells.indexOf(td) + cols, cells.length - 1)];
      setResizePreviewPx(target ? (target.getBoundingClientRect().right - td.getBoundingClientRect().right) / zoom : 0);
    }
    function onUp() {
      document.removeEventListener('mousemove', onMove);
      document.removeEventListener('mouseup', onUp);
      const cols = resizeColsRef.current;
      resizeRef.current = null;
      resizeColsRef.current = 0;
      setResizeExtend(0);
      if (cols > 0) stretchDaysRef.current(cols);
    }
    document.addEventListener('mousemove', onMove);
    document.addEventListener('mouseup', onUp);
  }

  const eindeWerkenProjects = usePlanningStore((s) => s.eindeWerkenProjects);
  const gevorderdFases = usePlanningStore((s) => s.gevorderdFases);
  const isPlaceholder = task.id < 0 && !task.is_easykit;
  const projectId = task.project_id ? task.project_id[0] : 0;
  const isEasykit = !!task.is_easykit;
  // EasyKit tasks on project 955 must NOT take the holiday branch — they
  // need chips, red fill, and the EASYKIT pill.
  const isHoliday = projectId === 955 && !isEasykit;
  const isSharedInGroup = !!task.x_studio_alle_projectleiders
    && !!task.x_studio_projectleider
    && groupPlId !== undefined
    && task.x_studio_projectleider[0] !== groupPlId;

  // Mobile board: a card tap opens the action sheet instead of exposing inline
  // icon controls (which misfire under a finger). Only on the standalone board
  // (`editable` undefined) — the dashboard Inplannen modal is desktop/drag-first.
  const mobileSheetEnabled = isMobile && (editable === undefined || overview) && !isEasykit && !isHoliday && !isPlaceholder;

  const sharedIcon = isSharedInGroup ? (
    <svg className="w-3 h-3 text-gray-400 shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-label="Gedeeld met alle projectleiders">
      <title>Gedeeld met alle projectleiders</title>
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0z" />
    </svg>
  ) : null;


  const color = isEasykit
    ? { bg: 'bg-red-50', border: 'border-l-red-500', dot: 'bg-red-500' }
    : isHoliday
    ? { bg: 'bg-purple-50', border: 'border-l-purple-500', dot: 'bg-purple-500' }
    : (colorOverride ?? getProjectColor(projectId));

  const soId = task.sale_order_id ? task.sale_order_id[0] : 0;
  const parentId = task.parent_id ? task.parent_id[0] : 0;
  const showEindeWerken = eindeWerkenProjects.has(projectId);
  const showGevorderd = !showEindeWerken && gevorderdFases.has(parentId);
  const isCompleted = showEindeWerken || showGevorderd;

  const projectName = projectNameMap.get(soId) || (task.project_id ? displayProjectName(task.project_id[1], { stripTemplateSuffix: true }) : 'Geen project');
  const soName = soNameMap.get(soId) || '';

  const werfId = task.x_studio_afleveradres ? task.x_studio_afleveradres[0] : 0;
  const werfLocation = werfMap.get(werfId) || '';

  const hasParent = !!task.parent_id;

  function navigateToProject() {
    setActiveTask(task.id);
    navigate(`/project/${projectId}?task=${task.id}`);
  }

  const createMode = usePlanningStore((s) => s.createMode);
  const scheduleProjectMode = usePlanningStore((s) => s.scheduleProjectMode);
  const isAnyModeActive = !!createMode || !!scheduleProjectMode;

  const { attributes, listeners, setNodeRef: setDragRef, transform, isDragging } = useDraggable({
    id: `${task.id}:${installerId || 0}`,
    data: { taskId: task.id, installerId: installerId || 0 },
    disabled: isAnyModeActive || installerDropdownOpen || isHoliday || isEasykit || !effectiveEditable || isPlaceholder || mobileSheetEnabled,
  });

  const dragStyle: React.CSSProperties | undefined = transform ? {
    transform: `translate(${transform.x / zoom}px, ${transform.y / zoom}px)`,
    opacity: 0.6,
    zIndex: 50,
    position: 'relative',
  } : undefined;

  function handleEasykitClick() {
    if (isAnyModeActive || !isEasykit) return;
    if (task._easykitDayTasks?.length) setShowEasykitModal(true);
  }

  function handleMultiDay(e: React.MouseEvent) {
    e.stopPropagation();
    setShowPopup(true);
  }

  function handleArrowClick(e: React.MouseEvent) {
    e.stopPropagation();
    navigateToProject();
  }

  async function handleUnschedule(e: React.MouseEvent) {
    e.stopPropagation();
    const store = usePlanningStore.getState();
    const prevTasks = store.tasks;
    usePlanningStore.setState({ tasks: prevTasks.filter((t) => t.id !== task.id) });
    try {
      await updatePlanningTask(task.id, { date_deadline: '', x_studio_installateur: [] });
      onTaskMoved?.(); // task is archived → host re-pulls board + "Verwijderde taken"
    } catch {
      usePlanningStore.setState({ tasks: prevTasks, error: 'Taak uit planning halen mislukt' });
    }
  }

  async function stretchDays(count: number) {
    if (!task.parent_id || !task.date_deadline) return;
    // One entry per board column dragged across — Sundays have no column.
    const newDates = nextBoardDateKeys(task.date_deadline.slice(0, 10), count);
    const placeholders: OdooTask[] = newDates.map((dk, i) => ({ ...task, id: -(task.id * 100 + i + 1), date_deadline: dk }));
    const store = usePlanningStore.getState();
    usePlanningStore.setState({ tasks: [...store.tasks, ...placeholders] });
    try {
      await saveMultiday({
        source_task_id: task.id,
        parent_id: task.parent_id[0],
        new_dates: newDates,
        remove_task_ids: [],
      });
      await fetchPlanning(undefined, { silent: true });
    } catch {
      usePlanningStore.setState({
        tasks: usePlanningStore.getState().tasks.filter((t) => t.id > 0),
        error: 'Dagen toevoegen mislukt',
      });
    }
  }

  return (
    <>
      <div
        ref={(node) => { setDragRef(node); cardRef.current = node; }}
        style={dragStyle}
        onClick={(e) => {
          e.stopPropagation();
          // Open the sheet on CLICK (not pointerup): opening on pointerup mounts the
          // sheet before the follow-up click dispatches, so that click lands on the
          // sheet backdrop and closes it again — the "flash". Click has no trailing
          // event, so the sheet stays open.
          if (mobileSheetEnabled) setShowSheet(true);
        }}
        onPointerUp={() => {
          if (isDragging || mobileSheetEnabled) return;
          if (isEasykit) { handleEasykitClick(); return; }
          if (!isPlaceholder && faseId) usePlanningStore.setState({ lastClickedFaseId: faseId });
        }}
        {...((!isAnyModeActive && !installerDropdownOpen && !isHoliday && !isEasykit && !isPlaceholder && effectiveEditable && !mobileSheetEnabled) ? { ...attributes, ...listeners } : {})}
        className={`relative group w-full text-left p-1.5 rounded border-l-3 ${color.border} ${color.bg} hover:shadow-sm transition-shadow mb-1 last:mb-0 ${
          !isAnyModeActive && !installerDropdownOpen && !isHoliday && !isEasykit && !isPlaceholder && effectiveEditable && !mobileSheetEnabled ? 'cursor-grab active:cursor-grabbing' : ''
        } ${mobileSheetEnabled ? 'cursor-pointer' : ''} ${isDragging ? 'shadow-lg' : ''} ${isCompleted ? 'planning-hatching opacity-60' : ''} ${isEasykit && task._easykitDayTasks?.length ? 'cursor-pointer' : ''} ${isPlaceholder ? 'opacity-50 animate-pulse pointer-events-none' : ''} ${isFaseHighlighted ? 'brightness-[0.85]' : ''}`}
      >
        {/* Trash — absolute bottom-right, hover-only. Hidden on the mobile board;
            "uit planning halen" lives in the action sheet with a confirm step. */}
        {!isAnyModeActive && !isEasykit && !isPlaceholder && effectiveEditable && !mobileSheetEnabled && (
          <button
            type="button"
            onClick={handleUnschedule}
            onPointerDown={(e) => e.stopPropagation()}
            className="absolute bottom-1 right-1 opacity-0 pointer-events-none group-hover:opacity-100 group-hover:pointer-events-auto focus:opacity-100 focus:pointer-events-auto transition-opacity p-1 rounded text-td-muted hover:text-red-500 hover:bg-white/80 z-10"
            aria-label="Uit planning halen"
            title="Uit planning halen"
          >
            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden="true">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
            </svg>
          </button>
        )}

        {task.isInterventie && <InterventieBand />}
        {/* Card content — clickable to navigate */}
        {(isAnyModeActive || (!canViewProject && !task._easykitDayTasks?.length)) ? (
          <div className={`w-full text-left ${isAnyModeActive ? 'pointer-events-none' : 'select-text'}`}>
            <div className="flex items-center gap-1">
              <div className="flex items-center gap-1 min-w-0 flex-1">
                <span className={`w-2 h-2 rounded-full ${color.dot} shrink-0`} />
                {isEasykit && (
                  <span className="text-[9px] px-1 py-0.5 rounded bg-red-500 text-white font-bold shrink-0">EASYKIT</span>
                )}
                {sharedIcon}
                <p className="text-xs truncate"><span className="font-bold text-td-navy">{projectName}</span>{soName && <span className="text-td-muted font-normal"> &middot; {soName}</span>}</p>
              </div>
              {showGevorderd && (
                <span className="text-[9px] px-1.5 py-0.5 rounded-full bg-green-600 text-white shrink-0">Gevorderd</span>
              )}
              {showEindeWerken && (
                <span className="text-[9px] px-1.5 py-0.5 rounded-full bg-blue-600 text-white shrink-0">Einde werken</span>
              )}
            </div>
            <p className="text-[10px] text-td-muted truncate ml-3">{task.name}</p>
            {werfLocation && (
              <p className="text-[10px] text-td-light truncate ml-3">{werfLocation}</p>
            )}
          </div>
        ) : (
          <div className="w-full text-left">
            <div className="flex items-center gap-1">
              <div className="flex items-center gap-1 min-w-0 flex-1">
                <span className={`w-2 h-2 rounded-full ${color.dot} shrink-0`} />
                {isEasykit && (
                  <span className="text-[9px] px-1 py-0.5 rounded bg-red-500 text-white font-bold shrink-0">EASYKIT</span>
                )}
                {sharedIcon}
                <p className="text-xs truncate"><span className="font-bold text-td-navy">{projectName}</span>{soName && <span className="text-td-muted font-normal"> &middot; {soName}</span>}</p>
              </div>
              {showGevorderd && (
                <span className="text-[9px] px-1.5 py-0.5 rounded-full bg-green-600 text-white shrink-0">Gevorderd</span>
              )}
              {showEindeWerken && (
                <span className="text-[9px] px-1.5 py-0.5 rounded-full bg-blue-600 text-white shrink-0">Einde werken</span>
              )}
            </div>
            <p className="text-[10px] text-td-muted truncate ml-3">{task.name}</p>
            {werfLocation && (
              <p className="text-[10px] text-td-light truncate ml-3">{werfLocation}</p>
            )}
          </div>
        )}

        {/* Installer chips — read-only on the mobile board (no inline edit pencil;
            editing happens through the action sheet). */}
        {!isHoliday && (
          <InstallerChips
            task={task}
            cardBg={color.bg}
            isEasykit={isEasykit}
            isAnyModeActive={isAnyModeActive}
            editable={effectiveEditable && !mobileSheetEnabled}
            projectName={projectName}
          />
        )}

        {/* Resize handle — right edge, drag to extend days. Hidden on mobile (use
            "meerdere dagen plannen" in the sheet). */}
        {effectiveEditable && hasParent && !isAnyModeActive && !isHoliday && !isEasykit && !isPlaceholder && !mobileSheetEnabled && (
          <div
            onMouseDown={handleResizeStart}
            onPointerDown={(e) => e.stopPropagation()}
            className="absolute right-0 top-0 bottom-0 w-2 cursor-col-resize opacity-0 pointer-events-none group-hover:opacity-100 group-hover:pointer-events-auto transition-opacity hover:bg-td-blue/20 rounded-r"
            title="Sleep om dagen toe te voegen"
          />
        )}
        {resizeExtend > 0 && (
          <div
            className="absolute top-0 bottom-0 bg-teal-400/30 border-2 border-dashed border-teal-500 rounded pointer-events-none"
            style={{ left: '100%', width: resizePreviewPx ? `${resizePreviewPx}px` : `${resizeExtend * 100}%` }}
          />
        )}

        {/* Action bar — calendar + naar project. Hidden on mobile; the sheet carries
            these actions as labeled rows. */}
        {!isAnyModeActive && !isEasykit && !mobileSheetEnabled && (
          <div className="flex items-center gap-1 mt-1 ml-3" onClick={(e) => e.stopPropagation()}>
            {onCardClick && (
              <button
                type="button"
                onClick={() => onCardClick(task)}
                onPointerDown={(e) => e.stopPropagation()}
                className="p-1 rounded text-td-muted hover:text-td-blue hover:bg-white/80 transition-colors"
                aria-label="Details en beschrijving"
                title="Details en beschrijving"
              >
                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden="true">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 4H7a2 2 0 01-2-2V6a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V18a2 2 0 01-2 2z" />
                </svg>
              </button>
            )}
            {effectiveEditable && hasParent && (
              <button
                type="button"
                onClick={handleMultiDay}
                onPointerDown={(e) => e.stopPropagation()}
                className="p-1 rounded text-td-muted hover:text-td-blue hover:bg-white/80 transition-colors"
                aria-label="Meerdere dagen plannen"
                title="Meerdere dagen plannen"
              >
                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden="true">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" />
                </svg>
              </button>
            )}
            {/* "Naar project" opens that card's project — useful in the scheduler too
                for jumping to another project's dashboard. */}
            {canViewProject && (
              <button
                type="button"
                onClick={handleArrowClick}
                onPointerDown={(e) => e.stopPropagation()}
                className="p-1 rounded text-td-muted hover:text-td-blue hover:bg-white/80 transition-colors"
                aria-label="Naar project"
                title="Naar project"
              >
                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden="true">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 6H6a2 2 0 00-2 2v10a2 2 0 002 2h10a2 2 0 002-2v-4M14 4h6m0 0v6m0-6L10 14" />
                </svg>
              </button>
            )}
          </div>
        )}
      </div>

      {showPopup && (
        <MultiDayPopup
          task={task}
          onClose={() => setShowPopup(false)}
          onSaved={() => fetchPlanning(undefined, { silent: true })}
        />
      )}

      {showEasykitModal && task._easykitDayTasks && (
        <EasykitTourModal
          tasks={task._easykitDayTasks}
          installerName={installerId ? (userMap.get(installerId) || `#${installerId}`) : 'Niet toegewezen'}
          dateLabel={task.date_deadline ? task.date_deadline.slice(0, 10) : ''}
          onClose={() => setShowEasykitModal(false)}
        />
      )}

      {showSheet && (
        <PlanningTaskSheet
          task={task}
          installerId={installerId || 0}
          canEdit={effectiveEditable}
          onClose={() => setShowSheet(false)}
          onOpenDetails={(t) => { if (onCardClick) onCardClick(t); else navigateToProject(); }}
          // Close the sheet BEFORE opening MultiDayPopup so the popup (z-50) isn't
          // trapped behind the sheet backdrop (z-60).
          onMultiDay={() => { setShowSheet(false); setShowPopup(true); }}
        />
      )}
    </>
  );
}
